import "./Geo.scss";
import React, { useCallback, useEffect, useState } from "react";
import * as R from "ramda";
import { useCompanyInfo } from "../../../redux/company";
import {
  formatDate,
  MonthPicker,
  startDateRange,
} from "../../../Components/MonthPicker/MonthPicker";
import { BPMTable, Dropdown, DropdownToggleType, FullPageSpinner, Page } from "../../../Components";
import GeoDataCards from "./GeoDataCards";
import { getGeoTableHeaders } from "./GeoConfig";
import { StateMap } from "../../../Components/StateMap/StateMap";
import { RouteComponentProps } from "@reach/router";
import { AMC_TEST_INSTANCE_ID } from "../../Insights";
import { abbreviateNumber } from "../../../utils/format-utils";
import { useSetError } from "../../../redux/modals";
import { AmcLambdaFetch, awaitJSON } from "../../../utils/fetch-utils";

interface AmcAvailableFilters {
  granularity: string[];
  months?: string[];
  weeks?: string[];
}

interface GeoTableData {
  conversionRate: number;
  customersConverted: number;
  dpv: number;
  dpvRate: number;
  impressions: number;
  ntbCustomers: number;
  ntbPurchaseRate?: number;
  ntbRate?: number;
  ntbRepeatCustomers?: number;
  ntbRepeatRate: number;
  orders: number;
  reach: number;
  revenue: number;
  state: string;
}

export interface GetAmcGeoResponse {
  availableFilters: AmcAvailableFilters;
  data: GeoTableData[];
  total: GeoTableData;
}

/*eslint-disable no-empty-pattern*/
const Geo = ({}: RouteComponentProps): JSX.Element => {
  let { cid, amc_instance_id: amcInstanceId } = useCompanyInfo();
  if (cid === "tinuititest") {
    amcInstanceId = AMC_TEST_INSTANCE_ID;
  }
  const [stateSelection, updateStateSelection] = useState("grandTotal");
  const [dataPointSelection, updateDataPointSelection] = useState("impressions");
  const setError = useSetError();

  // Previous month reports not prepared until the 15th of current month
  const monthsBack = new Date().getDate() < 15 ? 2 : 1;
  const [startDateSelection, updateStartDateSelection] = useState(startDateRange(monthsBack + 2));
  const [endDateSelection, updateEndDateSelection] = useState(startDateRange(monthsBack));
  const [dateOptions, updateDateOptions] = useState([{ value: "" }]);
  const [dateRange, updateDateRange] = useState({
    endDate: formatDate(endDateSelection, "end"),
    startDate: formatDate(startDateSelection, "start"),
  });

  const [geoData, setGeoData] = useState<GetAmcGeoResponse | null>(null);
  const [maxValues, setMaxValues] = useState({
    dpvRate: 0,
    conversionRate: 0,
    ntbRepeatRate: 0,
    purchaseRate: 0,
  });
  const [loading, setLoading] = useState(true);

  const sanitizeTableData = (data: GeoTableData[]): GeoTableData[] => {
    return data.map(row => {
      return {
        ...row,
        dpvRate: row.dpvRate ? parseFloat(String(row.dpvRate)) : 0,
        conversionRate: row.conversionRate ? parseFloat(String(row.conversionRate)) : 0,
        ntbRepeatRate: row.ntbRepeatRate ? parseFloat(String(row.ntbRepeatRate)) : 0,
        ntbPurchaseRate: row.ntbPurchaseRate ? parseFloat(String(row.ntbPurchaseRate)) : 0,
      };
    });
  };

  useEffect(() => {
    const fetchGeoData = async () => {
      if (!amcInstanceId) {
        return;
      }
      try {
        setLoading(true);
        const response = await AmcLambdaFetch("/getGeoReport", {
          params: {
            amcInstanceId,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            duration: "monthly",
            state: stateSelection === "grandTotal" ? null : stateSelection,
          },
        });
        const json: GetAmcGeoResponse = await awaitJSON(response);
        const sanitized: GetAmcGeoResponse = {
          ...json,
          data: sanitizeTableData(json.data),
          total: sanitizeTableData([json.total])[0],
        };
        setGeoData(sanitized);

        setMaxValues({
          dpvRate: Math.max(...json.data.map(row => row.dpvRate || 0)),
          conversionRate: Math.max(...json.data.map(row => row.conversionRate || 0)),
          ntbRepeatRate: Math.max(...json.data.map(row => row.ntbRepeatRate || 0)),
          purchaseRate: Math.max(...json.data.map(row => row.ntbPurchaseRate || 0)),
        });
      } catch (error) {
        setError({
          title: "Error fetching Geo data",
          message: "There was an error fetching the Geo data. Please try again later.",
        });
        setGeoData(null);
      } finally {
        setLoading(false);
      }
    };
    if (new Date(dateRange.startDate) > new Date(dateRange.endDate)) {
      setError({
        title: "Invalid Date Range",
        message: "Please select a valid date range where the start date is before the end date.",
      });
      const start = startDateRange(monthsBack + 2);
      const end = startDateRange(monthsBack);
      updateStartDateSelection(start);
      updateEndDateSelection(end);
      updateDateRange({
        startDate: formatDate(start, "start"),
        endDate: formatDate(end, "end"),
      });
      return;
    }
    fetchGeoData();
  }, [amcInstanceId, dateRange, monthsBack, setError, stateSelection]);

  const bottomRenderer = ({ data, style, classes }): JSX.Element => {
    let newData;
    newData =
      data === "Grand Total" || R.includes("%", data) ? data : abbreviateNumber(parseFloat(data));

    return (
      <div className={classes.join(" ")} style={style}>
        {newData}
      </div>
    );
  };

  const formatTotalCells = data => {
    data.dpvRate = `${data.dpvRate}%`;
    data.conversionRate = `${data.conversionRate}%`;
    data.ntbRepeatRate = `${data.ntbRepeatRate}%`;
    data.ntbPurchaseRate = `${data.ntbPurchaseRate}%`;
  };

  // Cloned to make formatting changes for avgOrderSize and pruchaseRate
  const tableGrandTotal = R.clone(geoData?.total);
  if (tableGrandTotal) {
    formatTotalCells(tableGrandTotal);
  }

  useEffect(() => {
    if (geoData) {
      const months = geoData?.availableFilters.months;
      const options = months?.map(m => {
        return { value: m };
      });

      updateDateOptions(options || []);
    }
  }, [geoData]);

  const geoTableData = geoData?.data || [];
  const geoGrandTotal = geoData?.total || ({} as GeoTableData);
  const dataCardData =
    stateSelection === "grandTotal"
      ? geoGrandTotal
      : geoTableData.find(d => d.state === stateSelection);

  const stateMapTooltipFormatter = useCallback((dataPoint, dataPoints, d) => {
    return `${dataPoint
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/ntb/i, "NTB")
      .replace(/dpv/i, "DPV")}: ${abbreviateNumber(dataPoints[d.properties.name])}`;
  }, []);

  return (
    <Page minHeight="600px" pageType="State Geo" title="Geo">
      {!geoData || (loading && <FullPageSpinner />)}
      <div className="geo-page">
        <div className="geo-page-header">
          <div className="back-arrow"></div>
          <MonthPicker
            dateOptions={dateOptions}
            endDateSelection={endDateSelection}
            startDateSelection={startDateSelection}
            updateDateRange={updateDateRange}
            updateEndDateSelection={updateEndDateSelection}
            updateStartDateSelection={updateStartDateSelection}
          />
        </div>

        <div className="basic-card geo-map-and-data">
          <div className="map-section-content">
            <div className="dropdown-state-map-wrapper">
              <Dropdown
                type={DropdownToggleType.OUTLINED}
                design="secondary"
                onChange={option => updateDataPointSelection(option)}
                options={[
                  { label: "Impressions", value: "impressions" },
                  { label: "Reach", value: "reach" },
                  { label: "DPV", value: "dpv" },
                  { label: "Customers Converted", value: "customersConverted" },
                  { label: "Orders", value: "orders" },
                  { label: "Revenue", value: "revenue" },
                  { label: "NTB Customers", value: "ntbCustomers" },
                  { label: "DPV Rate", value: "dpvRate" },
                  { label: "Conversion Rate", value: "conversionRate" },
                  { label: "NTB Repeat Rate", value: "ntbRepeatRate" },
                ]}
                value={dataPointSelection}
              />
              {!loading && (
                <StateMap
                  data={geoTableData.map(row => ({ ...row }))}
                  dataPoint={dataPointSelection}
                  resetValue="grandTotal"
                  tooltipFormatter={stateMapTooltipFormatter}
                  updateSelection={updateStateSelection}
                />
              )}
            </div>
            <GeoDataCards
              geoCardData={dataCardData || ({} as any)}
              title={stateSelection === "grandTotal" ? "Grand Total" : stateSelection}
            />
          </div>
        </div>
        <div className="basic-card">
          <div className="table-wrapper">
            {geoData ? (
              <BPMTable
                data={geoTableData}
                filterBar={false}
                headers={getGeoTableHeaders(maxValues)}
                noRowsRenderer={() => <div className="noFailures">No data to show</div>}
                pagination={true}
                totals={tableGrandTotal}
                totalsRenderer={bottomRenderer}
              />
            ) : (
              <FullPageSpinner />
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default Geo;
